/**
 * managedAxios — Drop-in replacement for `import axios from 'axios'`.
 *
 * Routes all requests through the requestManager concurrency limiter + dedupe.
 * Keeps the same surface API as axios so page-level code needs only an import change.
 * Auto-tags GET requests with the 'page' cancel group for route-change cancellation.
 */
import axios from 'axios';
import { managedRequest, getQueueStats, getLogRing } from './requestManager';
import { BACKEND_URL } from './backendUrl';

// Shared auth interceptor — mirrors AuthContext token injection
const rawAxios = axios.create({ baseURL: BACKEND_URL });
rawAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
rawAxios.interceptors.response.use(r => r, error => {
  if (error.response?.status === 403) window.dispatchEvent(new Event('access-permissions-changed'));
  return Promise.reject(error);
});

// Auto-attach page cancel group for GET requests (route-change cancellation)
function _autoTag(config) {
  if (!config._cancelGroup) config._cancelGroup = 'page';
  return config;
}

// Managed methods
const managedAxios = {
  get: (url, config = {}) => managedRequest(rawAxios, _autoTag({ ...config, method: 'get', url })),
  post: (url, data, config = {}) => managedRequest(rawAxios, { ...config, method: 'post', url, data }),
  put: (url, data, config = {}) => managedRequest(rawAxios, { ...config, method: 'put', url, data }),
  delete: (url, config = {}) => managedRequest(rawAxios, { ...config, method: 'delete', url }),
  patch: (url, data, config = {}) => managedRequest(rawAxios, { ...config, method: 'patch', url, data }),

  // create() returns a managed sub-instance with auto-tagged GETs
  create: (instanceConfig = {}) => {
    const base = instanceConfig.baseURL || '';
    return {
      get: (url, config = {}) => managedRequest(rawAxios, _autoTag({ ...config, method: 'get', url: `${base}${url}` })),
      post: (url, data, config = {}) => managedRequest(rawAxios, { ...config, method: 'post', url: `${base}${url}`, data }),
      put: (url, data, config = {}) => managedRequest(rawAxios, { ...config, method: 'put', url: `${base}${url}`, data }),
      delete: (url, config = {}) => managedRequest(rawAxios, { ...config, method: 'delete', url: `${base}${url}` }),
      patch: (url, data, config = {}) => managedRequest(rawAxios, { ...config, method: 'patch', url: `${base}${url}`, data }),
      defaults: { headers: { common: {} }, ...instanceConfig },
      interceptors: {
        request: { use: () => {}, eject: () => {} },
        response: { use: () => {}, eject: () => {} },
      },
    };
  },

  // Proxy defaults so AuthContext's `axios.defaults.headers.common[...] = ...` doesn't crash
  defaults: axios.defaults,

  // Proxy interceptors so any global interceptor setup doesn't crash
  interceptors: axios.interceptors,
};

export default managedAxios;

// ── WEB6P proof: expose on window for burst testing ──
if (typeof window !== 'undefined') {
  window.__managedAxios = managedAxios;
  window.__rmStats = () => ({ stats: getQueueStats(), log: getLogRing() });
}
